<template>
  <!-- 리스트 보기 버튼 -->
  <div v-if="showCards" class="pst-as">
    <button type="button" class="hamburger-btn" @click="toggleCards">
      <IconHamburger></IconHamburger>
    </button>
  </div>

  <div v-else class="container-cardlist">
    <div class="head flex-btw">
      <div v-if="title" class="tit-h5 accent">{{ title }}</div>
      <CloseIcon v-if="usingCloseBtn" class="close-icon" @click="toggleCards" />
    </div>

    <div class="line-tab-wrap">
      <div :class="['line-tab', tab === 'ALL' ? 'on' : '']" @click="handleTab('ALL')">
        {{ t('label_all') }}
      </div>
      <div :class="['line-tab', tab === 'FAVORITE' ? 'on' : '']" @click="handleTab('FAVORITE')">
        {{ t('favorite_interpretation_list') }}
      </div>
    </div>

    <!--리스트에 카드가 없을때-->
    <div class="no-data" v-if="listCall.length === 0">
      <div class="icon-area mb-3">
        <img :src="'/images/mobile/visual/no-data.svg'" alt="" />
      </div>
      <p>
        <template v-if="tab === 'ALL'">
          {{ t('interpretation_history_empty') }}
        </template>
        <template v-else-if="tab === 'FAVORITE'">
          {{ t('interpretation_history_empty') }}
        </template>
      </p>
    </div>

    <div v-else class="wrapper-cardlist" ref="refCardList">
      <InterpretationCard
        v-for="(item, i) in listCall"
        :key="item.id ?? i"
        :call-info="item"
        :unit-price-list="unitPriceList"
        :using-actions="usingActions"
        @select="selectInterpretationCard"
        @refresh="refreshCards"
        @toggle-favorite="toggleFavorite"
      />
    </div>
    <div ref="refScrollPager" style="display: none"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { addFavorite, deleteFavorite, fetchSelfServiceContractPricingV2 } from '@/api/apiService';
import CloseIcon from '@/components/icons/IconClose.vue';
import IconHamburger from '@/components/jh-app/icons/IconHamburger.vue';
import InterpretationCard from '@/components/shared/composite/InterpretationCard.vue';
import { IResponseCallManage, IResponseContractCustomerPricing, IResponseMultiCallsDashboard } from '@/types/response';

const { t } = useI18n();

const showCards = defineModel<boolean>('cards', { default: false });
const isFavorite = defineModel<boolean>('isFavorite', { default: false });

withDefaults(
  defineProps<{
    title?: string;
    usingActions?: boolean;
    usingCloseBtn?: boolean;
    listCall: IResponseCallManage[];
  }>(),
  {
    title: '',
    usingActions: false,
    usingCloseBtn: false,
    listCall: () => [] as IResponseCallManage[],
  }
);

const emit = defineEmits<{
  select: [callInfo: IResponseMultiCallsDashboard]; // 통화 선택
  refresh: [isGetMore: boolean]; // 통화 목록 갱신
  scrollEnd: []; // 통화 목록 추가 조회
}>();

const refCardList = ref<HTMLElement>(); // 카드 목록 DOM
const refScrollPager = ref<HTMLElement>(); // 스크롤 페이징을 위한 DOM (보이면 추가 조회)
const unitPriceList = ref<IResponseContractCustomerPricing[]>([]);

const tab = ref<'ALL' | 'FAVORITE'>('ALL');

const handleTab = (val: 'ALL' | 'FAVORITE') => {
  tab.value = val;
  refreshCards();
};

const toggleCards = () => {
  showCards.value = !showCards.value;
};

const refreshCards = () => {
  isFavorite.value = tab.value === 'FAVORITE';
  emit('refresh', false);
};

// 즐겨찾기 설정/해제
const toggleFavorite = async (callInfo: IResponseCallManage) => {
  if (!callInfo.isFavorite) {
    await addFavorite('QR', callInfo.code);
  } else {
    await deleteFavorite('QR', callInfo.code);
  }

  refreshCards();
};

// 스크롤 페이징 옵저버 추가
const addObserverScrollPager = () => {
  const observer = new IntersectionObserver(
    async ([entry]) => {
      if (entry.isIntersecting) {
        emit('scrollEnd');
      }
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    }
  );
  if (refScrollPager.value) observer.observe(refScrollPager.value);
};

// 카드 선택
const selectInterpretationCard = (callInfo: IResponseMultiCallsDashboard) => {
  emit('select', callInfo);
};

const requestUnitPriceList = async () => {
  const { data = [] } = await fetchSelfServiceContractPricingV2();
  unitPriceList.value = data;
};

onMounted(async () => {
  await requestUnitPriceList();
  addObserverScrollPager();
});
</script>

<style scoped lang="scss">
.pst-as {
  position: absolute;
  right: 1rem;
  top: 1rem;
  .hamburger-btn {
    width: 2rem;
    height: 2rem;
    background: #fff;
    border: 1px solid $color-gray-40;
    border-radius: $size-s;

    @include respond-to(mobile-landscape) {
      display: none;
    }
  }
}

.container-cardlist {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;

  .head {
    padding: 1rem 2rem;
  }
  .no-data {
    padding: 40px 0;
  }
  @include respond-to(mobile-landscape) {
    .head {
      padding: 1rem 1.25rem;
      .close-icon {
        display: none;
      }
    }
    .wrapper-cardlist {
      padding-bottom: 40px;
      .itp-used-list {
        padding: 1rem 0.75rem 1rem 1.25rem;
      }
    }
  }
}

.container-filters {
  display: flex;
  gap: 1rem;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;

  p,
  label {
    white-space: nowrap;
  }
}

.wrapper-cardlist {
  display: flex;
  flex-direction: column;
  gap: $size-s;
  z-index: $z-canvas;
  padding: 0 1rem;
  @include respond-to(tablet) {
    padding: 0;
  }
}
@media (max-width: 768px) {
  .all-manage-con {
    .wrapper-cardlist {
      .itp-used-list {
        padding: 1rem 0;
      }
    }
  }
}
</style>
